import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronDown, LogOut, Package, User } from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import { useLanguage } from "../../contexts/LanguageContext";
import { clearOrderSession, useOrderSession } from "../../utils/orderAccountSession";
import { ICON_STROKE } from "../ui/icon";
import { cn } from "../../lib/utils";

/**
 * Header account control: the signed-in name with a menu (profile / orders / log out) for QR owners
 * and checkout accounts. When signed out it shows a "Login" button if `onLogin` is given.
 */
const AccountMenu = ({ onLogin, className }) => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const { isAuthenticated, user, logout } = useAuth();
  const orderSession = useOrderSession();
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const close = (e) => {
      if (!rootRef.current?.contains(e.target)) setOpen(false);
    };
    document.addEventListener("pointerdown", close);
    return () => document.removeEventListener("pointerdown", close);
  }, [open]);

  const customer = isAuthenticated;
  const signedIn = customer || Boolean(orderSession);

  if (!signedIn) {
    if (!onLogin) return null;
    return (
      <button type="button" className={cn("account-menu__login", className)} onClick={onLogin}>
        <User size={16} strokeWidth={ICON_STROKE} aria-hidden />
        {t("nav.login")}
      </button>
    );
  }

  const name = customer ? user?.profile?.name : orderSession.account?.name;
  const phone = customer ? user?.profile?.phone : orderSession.account?.phone;
  const firstName = name?.trim().split(/\s+/)[0] || t("nav.account");

  const go = (path) => {
    setOpen(false);
    navigate(path);
  };

  const handleLogout = () => {
    setOpen(false);
    logout();
    clearOrderSession();
    navigate("/");
  };

  return (
    <div ref={rootRef} className={cn("account-menu", className)}>
      <button
        type="button"
        className="account-menu__trigger"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        <span className="account-menu__avatar" aria-hidden>
          {firstName.charAt(0).toUpperCase()}
        </span>
        <span className="account-menu__name">{firstName}</span>
        <ChevronDown size={14} strokeWidth={ICON_STROKE} aria-hidden />
      </button>
      {open && (
        <div className="account-menu__panel" role="menu">
          <div className="account-menu__who">
            <span className="account-menu__who-label">{t("nav.signedInAs")}</span>
            <span className="account-menu__who-name">{name || firstName}</span>
            {phone && <span className="account-menu__who-phone">{phone}</span>}
          </div>
          {customer && (
            <button type="button" role="menuitem" className="account-menu__item" onClick={() => go("/profile")}>
              <User size={16} strokeWidth={ICON_STROKE} aria-hidden />
              {t("nav.profile")}
            </button>
          )}
          <button
            type="button"
            role="menuitem"
            className="account-menu__item"
            onClick={() => go(customer ? "/profile?tab=orders" : "/orders")}
          >
            <Package size={16} strokeWidth={ICON_STROKE} aria-hidden />
            {t("nav.myOrders")}
          </button>
          <button
            type="button"
            role="menuitem"
            className="account-menu__item account-menu__item--danger"
            onClick={handleLogout}
          >
            <LogOut size={16} strokeWidth={ICON_STROKE} aria-hidden />
            {t("nav.logout")}
          </button>
        </div>
      )}
    </div>
  );
};

export default AccountMenu;
